export const STALE_CHUNK_RELOAD_STORAGE_KEY = "ultrastock:stale-chunk-reloaded";

/** Browser signatures of a stale lazy-chunk load failure (issue #47). */
const STALE_CHUNK_MESSAGE_PATTERNS: readonly RegExp[] = [
  /failed to fetch dynamically imported module/i, // Chrome
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /loading chunk .+ failed/i // webpack
];

/** Keys the browser and Angular use to wrap the original error. */
const ERROR_WRAPPER_KEYS: readonly string[] = ["rejection", "originalError", "ngOriginalError", "error"];

export interface StaleChunkReloadDeps {
  storage: {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
  };
  reload: () => void;
}

export function isStaleChunkError(error: unknown): boolean {
  const seen = new Set<unknown>();
  let current: unknown = error;

  while (current !== null && typeof current === "object") {
    if (seen.has(current)) {
      return false;
    }
    seen.add(current);

    if (hasStaleChunkSignature(current)) {
      return true;
    }

    current = unwrapError(current);
  }

  return false;
}

function hasStaleChunkSignature(candidate: object): boolean {
  const record = candidate as Record<string, unknown>;

  if (record.name === "ChunkLoadError") {
    return true;
  }

  const message = record.message;
  return (
    typeof message === "string" &&
    STALE_CHUNK_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))
  );
}

function unwrapError(candidate: object): unknown {
  const record = candidate as Record<string, unknown>;

  for (const key of ERROR_WRAPPER_KEYS) {
    const inner = record[key];
    if (inner !== null && inner !== undefined) {
      return inner;
    }
  }

  return undefined;
}

const DEFAULT_STORAGE: StaleChunkReloadDeps["storage"] = {
  getItem: (key: string): string | null =>
    typeof sessionStorage === "undefined" ? null : sessionStorage.getItem(key),
  setItem: (key: string, value: string): void => {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(key, value);
    }
  }
};

const DEFAULT_RELOAD = (): void => {
  location.reload();
};

/**
 * true = the stale-chunk failure was handled by the one-time silent reload;
 * the caller must NOT report it to Sentry. false = not handled (not a chunk
 * error, or the reload was already spent) — the caller reports to Sentry.
 */
export function tryReloadOnceOnStaleChunk(error: unknown, deps?: StaleChunkReloadDeps): boolean {
  if (!isStaleChunkError(error)) {
    return false;
  }

  const storage = deps?.storage ?? DEFAULT_STORAGE;
  const reload = deps?.reload ?? DEFAULT_RELOAD;

  let alreadyReloaded: string | null;
  try {
    alreadyReloaded = storage.getItem(STALE_CHUNK_RELOAD_STORAGE_KEY);
  } catch {
    // Storage unavailable: fail safe so Sentry still sees the error.
    return false;
  }

  if (alreadyReloaded) {
    return false;
  }

  try {
    // Arm the guard BEFORE reloading so a failed reload cannot loop.
    storage.setItem(STALE_CHUNK_RELOAD_STORAGE_KEY, "1");
  } catch {
    // Could not arm the guard: do not risk a reload loop, let Sentry see it.
    return false;
  }

  reload();
  return true;
}
